<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { routes } from '@app/router/routes'
import {
  ACCOUNT_KYC_STATUS,
  useAccountAccessRestriction,
  useAccountStatusQuery,
  useReInitiateKycMutation,
  useUserProfileQuery,
} from '@modules/account'
import { useDashboardState, type DashboardState } from '@modules/dashboard'
import { useCurrentStepsQuery } from '@modules/onboarding'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Popover from '@uiKit/elements/Popover.vue'
import Tooltip from '@uiKit/elements/Tooltip.vue'
import { usePageHeaderStore } from '@uiKit/store/pageHeaderStore'
import NavbarProfileMenu from './NavbarProfileMenu.vue'

interface Props {
  unreadCount?: number
}

withDefaults(defineProps<Props>(), {
  unreadCount: 0,
})

const emit = defineEmits<{
  (e: 'toggle-mobile-nav'): void
  (e: 'open-notifications'): void
  (e: 'logout'): void
}>()

const router = useRouter()
const route = useRoute()
const profilePopover = ref<InstanceType<typeof Popover> | null>(null)

// Identity + page title block the designs put on the left of every screen.
const profileQuery = useUserProfileQuery()

// The dashboard greets the user (Figma 32590:59337): name on top,
// "Welcome Back!" as the title. Other screens show "name · email".
const isDashboardRoute = computed(() => route.path === routes.DASHBOARD)
const DASHBOARD_GREETING = 'Welcome Back!'

// A screen with a data-driven header (a holding's property name) overrides
// the route's static labels through usePageHeader.
const pageHeader = usePageHeaderStore()

const identityLine = computed(() => {
  if (pageHeader.eyebrow) return pageHeader.eyebrow
  // A route may set its own context line (order details: "Explore live opportunities").
  const eyebrow = route.meta.eyebrow
  if (typeof eyebrow === 'string' && eyebrow) return eyebrow
  const profile = profileQuery.data.value
  if (!profile) return ''
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ')
  if (isDashboardRoute.value) return name
  // Figma sets two spaces either side of the dot; the outer ones are
  // non-breaking so HTML whitespace collapsing doesn't eat them.
  return [name, profile.email].filter(Boolean).join('  ·  ')
})

// Routes declare their own label via `meta.title`. The fallback humanises the
// route name so a screen that hasn't been given one yet still shows something
// sensible rather than an empty bar.
const pageTitle = computed(() => {
  if (isDashboardRoute.value) return DASHBOARD_GREETING
  if (pageHeader.title) return pageHeader.title
  const explicit = route.meta.title
  if (typeof explicit === 'string' && explicit) return explicit
  const name = typeof route.name === 'string' ? route.name : ''
  if (!name) return ''
  return name
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')
})
const dashboardState = useDashboardState()
const accountStatusQuery = useAccountStatusQuery()
const stepsQuery = useCurrentStepsQuery()
const { isRestricted } = useAccountAccessRestriction()
const reInitiateMutation = useReInitiateKycMutation()

const isGuest = computed(() => dashboardState.value.kind === 'guest')

interface KycChip {
  label: string
  tone: 'verified' | 'pending' | 'review' | 'rejected' | 'idle'
  // Where clicking the chip takes the user. Tracked here so the chip stays
  // the canonical "where does this account need to go next?" surface.
  to: string | null
}

// Chip copy + tone derived from the shared DashboardState so the top-nav
// always speaks the same status the dashboard does — single source of truth.
// `invested` and `loading` deliberately map to null — the top-nav reads
// cleaner when investing is the default user state.
const PENDING_KYC_CHIP: KycChip = {
  label: 'Pending KYC',
  tone: 'pending',
  to: routes.ONBOARDING_WIZARD,
}

const UNDER_REVIEW_CHIP: KycChip = {
  label: 'Under Review',
  tone: 'review',
  to: routes.PROFILE,
}

const CHIP_FOR_STATE: Record<DashboardState['kind'], KycChip | null> = {
  'onboarding-pending': PENDING_KYC_CHIP,
  'in-review': UNDER_REVIEW_CHIP,
  'rekyc-required': { label: 'Renew identity', tone: 'pending', to: routes.ONBOARDING_WIZARD },
  paused: { label: 'Account paused', tone: 'rejected', to: routes.HELP },
  'regional-explore': { label: 'Explore mode', tone: 'review', to: routes.HELP },
  'approved-ready': { label: 'KYC verified', tone: 'verified', to: null },
  guest: null,
  invested: null,
  loading: null,
}

function chipForState(kind: DashboardState['kind']): KycChip | null {
  return CHIP_FOR_STATE[kind]
}

// KYC is verified but the investor hasn't finished the source-of-funds step —
// the account can't activate yet, so the chip must name what's outstanding.
const SOF_CHIP: KycChip = {
  label: 'Pending Source of Funds',
  tone: 'pending',
  to: routes.ONBOARDING_WIZARD,
}

const isSourceOfFundsDone = computed(() =>
  (stepsQuery.data.value?.steps ?? []).some(
    (step) => step.stepKey === 'source_of_funds' && step.status === 'completed',
  ),
)

// While onboarding isn't finished, the chip reflects the exact KYC stage
// rather than one generic "pending": investor hasn't submitted → "Pending
// KYC"; admin is verifying → "Under Review"; KYC cleared but SOF outstanding
// → "Pending Source of Funds".
function preActivationChip(): KycChip {
  const kyc = accountStatusQuery.data.value?.kycStatus
  if (kyc === ACCOUNT_KYC_STATUS.COMPLETED) {
    return isSourceOfFundsDone.value ? UNDER_REVIEW_CHIP : SOF_CHIP
  }
  if (kyc === ACCOUNT_KYC_STATUS.IN_REVIEW) return UNDER_REVIEW_CHIP
  return PENDING_KYC_CHIP
}

// A blocked-but-verified account renders the normal dashboard (so its state is
// approved-ready / invested), which would otherwise show a green "KYC verified"
// chip. Override it with a red "Account paused" chip — the top-nav status must
// reflect the restriction, not just KYC.
const RESTRICTED_CHIP: KycChip = {
  label: 'Account paused',
  tone: 'rejected',
  to: routes.HELP,
}

const kycChip = computed<KycChip | null>(() => {
  if (isRestricted.value) return RESTRICTED_CHIP
  const kind = dashboardState.value.kind
  if (kind === 'onboarding-pending' || kind === 'in-review') return preActivationChip()
  return chipForState(kind)
})

const isRekycChip = computed(
  () => !isRestricted.value && dashboardState.value.kind === 'rekyc-required',
)

// Re-KYC fires a network call before navigating, so the chip reflects that
// in-flight state — otherwise a tap reads as "nothing happened".
const isRenewing = computed(() => reInitiateMutation.isPending.value)

const chipTooltip = computed(() => {
  if (kycChip.value?.tone === 'verified') return 'Identity verified'
  if (isRekycChip.value) return 'Renew your identity verification'
  return 'Tap to view details'
})

const handleKycChipClick = async () => {
  // Re-KYC ("Renew identity") needs a primed session before the wizard opens:
  // re-initiate first, then route into the resubmit wizard. Pushing straight to
  // /onboarding mounts the wizard in normal mode, fires POST /onboarding/start,
  // and the backend rejects it ("Onboarding has already been completed") →
  // blank page. Mirrors StateReKycRequiredContainer / useInvestEligibility.
  if (isRekycChip.value) {
    if (reInitiateMutation.isPending.value) return
    try {
      await reInitiateMutation.mutateAsync()
    } catch {
      return
    }
    router.push({ path: routes.ONBOARDING_WIZARD, query: { mode: 'resubmit' } })
    return
  }

  const to = kycChip.value?.to
  if (to) router.push(to)
}
</script>

<template>
  <Box is="header" class="topnav" align="center" justify="between">
    <Box align="center" gap="2" class="topnav__left">
      <Box
        is="button"
        align="center"
        justify="center"
        class="topnav__icon-btn topnav__mobile-toggle"
        aria-label="Open menu"
        @click="emit('toggle-mobile-nav')"
      >
        <PiIcon name="bars" />
      </Box>

      <Box v-if="!isGuest" direction="col" class="topnav__identity">
        <Text v-if="identityLine" type="body" is="span" truncate class="topnav__identity-line">
          {{ identityLine }}
        </Text>
        <Text v-if="pageTitle" type="title-lg" is="h1" truncate class="topnav__title">
          {{ pageTitle }}
        </Text>
      </Box>
    </Box>

    <Box align="center" gap="3" class="topnav__right">
      <Tooltip v-if="kycChip" :content="chipTooltip">
        <Box
          is="button"
          align="center"
          gap="1"
          class="kyc-chip"
          :class="[`kyc-chip--${kycChip.tone}`, { 'kyc-chip--busy': isRenewing }]"
          :aria-label="kycChip.label"
          :aria-busy="isRenewing"
          @click="handleKycChipClick"
        >
          <Box class="kyc-chip__dot" :class="`kyc-chip__dot--${kycChip.tone}`" />
          <Text type="caption" weight="medium" is="span" class="kyc-chip__label">
            {{ kycChip.label }}
          </Text>
        </Box>
      </Tooltip>

      <Box
        is="button"
        align="center"
        justify="center"
        class="topnav__circle-btn"
        :aria-label="unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'"
        @click="emit('open-notifications')"
      >
        <PiIcon name="bell" />
        <Box v-if="unreadCount > 0" is="span" class="topnav__bell-dot" />
      </Box>

      <Box
        is="button"
        align="center"
        justify="center"
        class="topnav__circle-btn"
        aria-label="Account menu"
        @click="profilePopover?.toggle($event)"
      >
        <PiIcon name="user" />
      </Box>
    </Box>

    <Popover ref="profilePopover">
      <NavbarProfileMenu
        :is-guest="isGuest"
        @navigate="
          (to) => {
            profilePopover?.hide()
            router.push(to)
          }
        "
        @logout="
          () => {
            profilePopover?.hide()
            emit('logout')
          }
        "
      />
    </Popover>
  </Box>
</template>

<style scoped>
/* Fixed to the side nav's 80px brand row so the two bottom rules meet as one
   continuous line across the window. */
.topnav {
  height: 80px;
  padding: 0 42px;
  background: var(--p-surface-0);
  border-bottom: 1px solid var(--p-sliceal-gray-200);
  box-sizing: border-box;
  flex-shrink: 0;
}

.topnav__right {
  flex-shrink: 0;
}

/* The identity block takes the slack so long emails truncate instead of
   pushing the KYC chip and icons off the bar. */
.topnav__left {
  min-width: 0;
  flex: 1;
}

.topnav__left:empty {
  display: none;
}

.topnav__identity {
  min-width: 0;
}

/* Figma 32590:74305 tracks both lines at 0 — override Text's default 0.4px. */
.topnav__identity .topnav__identity-line {
  line-height: 18px;
  letter-spacing: 0;
}

.topnav__identity .topnav__title {
  font-family: var(--p-heading-font-family);
  line-height: 28px;
  letter-spacing: 0;
}

/* ── Utility icon buttons ─────────────────────── */
.topnav__icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--p-text-muted-color);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  padding: 0;
  position: relative;
}

.topnav__icon-btn:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.topnav__icon-btn:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

/* ── Bell + account (46px circles, Figma 32590:74309) ── */
.topnav__circle-btn {
  position: relative;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-sliceal-page-background);
  color: var(--p-primary-600);
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.topnav__circle-btn:hover {
  background: var(--p-primary-50);
  border-color: var(--p-primary-300);
}

.topnav__circle-btn:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.topnav__bell-dot {
  position: absolute;
  top: 9px;
  right: 10px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--p-danger-500);
  border: 1px solid var(--p-sliceal-gray-200);
}

/* ── KYC chip ─────────────────────────────────── */
.kyc-chip {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 999px;
  padding: 0.3rem 0.625rem;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.kyc-chip:hover {
  background: var(--p-surface-100);
}

.kyc-chip--busy {
  opacity: 0.65;
  cursor: progress;
  pointer-events: none;
}

.kyc-chip:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.kyc-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.kyc-chip__dot--verified {
  background: var(--p-success-500);
}

.kyc-chip__dot--pending {
  background: var(--p-warning-500);
  box-shadow: 0 0 0 3px var(--p-warning-100);
}

.kyc-chip__dot--review {
  background: var(--p-info-500);
}

.kyc-chip__dot--rejected {
  background: var(--p-danger-500);
}

.kyc-chip__dot--idle {
  background: var(--p-surface-400);
}

.kyc-chip--verified {
  background: var(--p-success-50);
  border-color: var(--p-success-200);
}

.kyc-chip--verified .kyc-chip__label {
  color: var(--p-success-700);
}

.kyc-chip--pending {
  background: var(--p-warning-50);
  border-color: var(--p-warning-200);
}

.kyc-chip--pending .kyc-chip__label {
  color: var(--p-warning-700);
}

.kyc-chip--review .kyc-chip__label,
.kyc-chip--idle .kyc-chip__label {
  color: var(--p-text-muted-color);
}

.kyc-chip--rejected {
  background: var(--p-danger-50);
  border-color: var(--p-danger-200);
}

.kyc-chip--rejected .kyc-chip__label {
  color: var(--p-danger-700);
}

/* ── Mobile toggle (hidden on desktop) ────────── */
.topnav__mobile-toggle {
  display: none;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
}

@media (max-width: 991px) {
  /* No side nav to line up with below this width — size to content again. */
  .topnav {
    height: auto;
    padding: 12px 1.5rem;
  }

  .topnav__mobile-toggle {
    display: inline-flex;
  }

  .kyc-chip {
    padding: 0.3rem 0.5rem;
  }
}

@media (max-width: 480px) {
  .kyc-chip__label {
    display: none;
  }

  .kyc-chip {
    padding: 0.4rem;
  }
}

@media (max-width: 640px) {
  .topnav {
    padding: 10px 1rem;
  }

  .topnav__circle-btn {
    width: 40px;
    height: 40px;
  }

  .topnav__bell-dot {
    top: 7px;
    right: 8px;
  }
}
</style>
